import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import InvestmentCTA from '@/components/InvestmentCTA';
import ReferralBanner from '@/components/ReferralBanner';
import ServiceImageCard from '@/components/ServiceImageCard';
import { QuoteCarousel } from '@/components/shared';
import { useLanguage } from '@/context/LanguageContext';
import { Check, TrendingUp, Shield, Users, BarChart3, Building2, Coins, Award, ArrowRight, ExternalLink } from 'lucide-react';
import { TypeAnimation } from 'react-type-animation';
import api from '@/utils/api';

// Quotes data
const quotes = [
  { text: "Not only is the universe stranger than we imagine, it is stranger than we can imagine.", author: "J. B. S. Haldane" },
  { text: "The Obstacle is the Way.", author: "Christoph Kampitsch" },
  { text: "Pure mathematics is, in its way, the poetry of logical ideas.", author: "Albert Einstein" },
  { text: "I am the master of my fate, I am the captain of my soul.", author: "William Ernest Henley" },
  { text: "The impediment to action advances action. What stands in the way becomes the way.", author: "Marcus Aurelius" },
  { text: "Wake up to reality! Nothing ever goes as planned in this accursed world.", author: "Madara Uchiha" }
];

const HomePage = () => {
  const { t, lang } = useLanguage();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const [investorForm, setInvestorForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    investment_amount: '',
    message: ''
  });
  const [investorStatus, setInvestorStatus] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.subscribeNewsletter(newsletterEmail);
      setNewsletterStatus('success');
      setNewsletterEmail('');
      setTimeout(() => setNewsletterStatus(''), 3000);
    } catch (error) {
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus(''), 3000);
    }
  };

  const handleInvestorSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.registerInvestor(investorForm);
      setInvestorStatus('success');
      setInvestorForm({
        full_name: '',
        email: '',
        phone: '',
        investment_amount: '',
        message: ''
      });
      setTimeout(() => setInvestorStatus(''), 5000);
    } catch (error) {
      setInvestorStatus('error');
      setTimeout(() => setInvestorStatus(''), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center pt-16 md:pt-24"
        data-testid="hero-section"
      >
        {/* Fondo desktop - anclado a la derecha */}
        <div 
          className="absolute top-24 left-0 right-0 bottom-0 hidden md:block"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_web-mejora-1/artifacts/3nexwnd4_7a40f4_c3cd8b99347e42349ae6fe852b8d205b~mv2.avif)',
            backgroundSize: 'contain',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        
        {/* Fondo móvil - imagen rotada 90 grados con proporciones originales */}
        <div 
          className="absolute top-0 left-0 right-0 bottom-0 md:hidden overflow-hidden"
          style={{
            background: 'linear-gradient(to bottom, #f8f8f8 0%, #ffffff 100%)'
          }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_realestate-fund-6/artifacts/qsxu12yq_1_LE_upscale_balanced_x4.webp"
            alt=""
            style={{
              position: 'absolute',
              top: '31%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(90deg) scale(1.80)',
              clipPath: 'inset(0 0 0 15%)'
            }}
          />
        </div>
        
        {/* Contenido desktop */}
        <div className="max-w-[1600px] mx-auto px-8 lg:px-12 w-full py-32 relative z-10 hidden md:block">
          <div className="max-w-4xl ml-16 mt-12">
            <h1 className="text-5xl lg:text-6xl font-bold leading-loose tracking-wider mb-8" data-testid="hero-title">
              <span className="text-black">{t('home.heroLine1')}</span><br />
              <span className="text-black">{t('home.heroLine2')}</span><br />
              <TypeAnimation
                key={lang}
                sequence={[
                  t('home.heroAnim1'),
                  6000,
                  t('home.heroAnim2'),
                  6000,
                  t('home.heroAnim3'),
                  6000,
                ]}
                wrapper="span"
                speed={10}
                className="text-black"
                repeat={Infinity}
              />
            </h1>
            <p className="text-xl mb-2 text-gray-800" data-testid="hero-subtitle">
              {t('home.heroSubtitle')}
            </p>
            <p className="text-xl mb-12 text-gray-700" data-testid="hero-description">
              {t('home.heroDesc')}
            </p>
            <div className="flex gap-4">
              <a 
                href="/invest" 
                className="bg-black text-white px-12 py-4 text-lg font-bold hover:bg-gray-900 transition-all duration-200 inline-flex items-center justify-center"
                data-testid="hero-invest-btn"
              >
                {t('home.joinInvestor')}
                <ArrowRight className="ml-2" size={20} />
              </a>
              <a 
                href="#services" 
                className="border-2 border-black text-black px-12 py-4 text-lg font-bold hover:bg-black hover:text-white transition-all duration-200 inline-flex items-center justify-center"
                data-testid="hero-explore-btn"
              >
                {t('home.exploreServices')}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </div>
          </div>
        </div>
        
        {/* Contenido móvil - caja premium más abajo */}
        <div className="w-full px-4 relative z-10 md:hidden flex flex-col items-center justify-start pt-[390px] pb-2">
          <div className="bg-gray-200/55 backdrop-blur-md p-6 rounded-2xl text-center w-full max-w-sm shadow-2xl border border-white/30" style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)' }}>
            <h1 className="text-[26px] font-black leading-tight tracking-wide mb-4 text-black whitespace-nowrap text-center" data-testid="hero-title-mobile">
              {t('home.heroLine1')}<br />
              {t('home.heroLine2')}<br />
              <TypeAnimation
                key={`mobile-${lang}`}
                sequence={[
                  t('home.heroAnim1'),
                  6000,
                  t('home.heroAnim2'),
                  6000,
                  t('home.heroAnim3'),
                  6000,
                ]}
                wrapper="span"
                speed={10}
                className="text-black"
                repeat={Infinity}
              />
            </h1>
            <p className="text-sm mb-1 text-gray-700">
              {t('home.heroSubtitle')}
            </p>
            <p className="text-sm mb-8 text-gray-600">
              {t('home.heroDesc')}
            </p>
            <div className="flex flex-col gap-3">
              <a 
                href="/invest" 
                className="bg-black text-white px-6 py-3 text-base font-bold hover:bg-gray-800 transition-all duration-200 inline-flex items-center justify-center rounded-xl shadow-lg"
                data-testid="hero-invest-btn-mobile"
              >
                {t('home.joinInvestor')}
                <ArrowRight className="ml-2" size={16} />
              </a>
              <a 
                href="#services" 
                className="border border-gray-400 text-gray-700 px-6 py-3 text-base font-semibold hover:bg-white/50 transition-all duration-200 inline-flex items-center justify-center rounded-xl"
                data-testid="hero-explore-btn-mobile"
              >
                {t('home.exploreServices')}
                <ArrowRight className="ml-2" size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section - Using shared component */}
      <QuoteCarousel quotes={quotes} />

      {/* Services Section - Premium Grid */}
      <section id="services" className="py-12 md:py-20 bg-white" data-testid="services-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <p className="text-sm tracking-[0.2em] text-gray-500 mb-3">{t('home.ourServices')}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight" data-testid="services-title">{t('home.whatWeDo')}</h2>
            <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto" data-testid="services-subtitle">
              {t('home.servicesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-3">
            {/* Real Estate */}
            <div className="relative h-80 md:h-96 overflow-hidden group cursor-pointer" data-testid="service-real-estate">
              <img 
                src="https://static.wixstatic.com/media/7a40f4_c04b84be1ba14ef690db59e9b903e86f~mv2.webp"
                alt="Real Estate"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">{t('home.realEstateTitle')}</h3>
                <p className="text-white/80 text-xs md:text-sm mb-3 md:mb-4 leading-relaxed">
                  {t('home.realEstateDesc')}
                </p>
                <a href="/invest" className="inline-flex items-center font-semibold text-xs md:text-sm bg-white/20 backdrop-blur-sm px-3 md:px-4 py-2 rounded hover:bg-white/30 transition-all">
                  {t('home.investNow')} <ArrowRight size={14} className="ml-2" />
                </a>
              </div>
            </div>

            {/* Quantitative */}
            <div className="relative h-80 md:h-96 overflow-hidden group cursor-pointer" data-testid="service-quantitative">
              <img 
                src="https://static.wixstatic.com/media/7a40f4_88fa79ae2304485fbf2ca2e3102f3840~mv2.webp"
                alt="Quantitative"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">{t('home.quantTitle')}</h3>
                <p className="text-white/80 text-xs md:text-sm mb-3 md:mb-4 leading-relaxed">
                  {t('home.quantDesc')}
                </p>
                <a href="/quantitative" className="inline-flex items-center font-semibold text-xs md:text-sm bg-white/20 backdrop-blur-sm px-3 md:px-4 py-2 rounded hover:bg-white/30 transition-all">
                  {t('home.exploreDAO')} <ArrowRight size={14} className="ml-2" />
                </a>
              </div>
            </div>

            {/* Crypto OTC */}
            <div className="relative h-80 md:h-96 overflow-hidden group cursor-pointer" data-testid="service-btc">
              <img 
                src="https://static.wixstatic.com/media/7a40f4_ea692eea04ec45729182fee25b746408~mv2.webp"
                alt="Crypto"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">{t('home.btcTitle')}</h3>
                <p className="text-white/80 text-xs md:text-sm mb-3 md:mb-4 leading-relaxed">
                  {t('home.btcDesc')}
                </p>
                <a href="/crypto-otc" className="inline-flex items-center font-semibold text-xs md:text-sm bg-white/20 backdrop-blur-sm px-3 md:px-4 py-2 rounded hover:bg-white/30 transition-all">
                  {t('home.getAccess')} <ArrowRight size={14} className="ml-2" />
                </a>
              </div>
            </div>

            {/* Luxury */}
            <div className="relative h-80 md:h-96 overflow-hidden group cursor-pointer" data-testid="service-offmarket">
              <img 
                src="https://static.wixstatic.com/media/7a40f4_061abe2b45074f7d9a8b5d506e389231~mv2.jpg"
                alt="Luxury"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">{t('home.offMarketSyndicate')}</h3>
                <p className="text-white/80 text-xs md:text-sm mb-3 md:mb-4 leading-relaxed">
                  {t('home.offMarketSyndicateDesc')}
                </p>
                <a href="/premium-assets" className="inline-flex items-center font-semibold text-xs md:text-sm bg-white/20 backdrop-blur-sm px-3 md:px-4 py-2 rounded hover:bg-white/30 transition-all">
                  {t('home.exploreDeals')} <ArrowRight size={16} className="ml-2" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== AAC Real Estate I - Premium Design ========== */}
      <InvestmentCTA 
        title={t('home.investCtaTitle')}
        returnTarget="+20%"
        subtitle={t('home.investCtaSubtitle')}
        features={[
          t('home.investCtaFeature1'),
          t('home.investCtaFeature2'),
          t('home.investCtaFeature3'),
          t('home.investCtaFeature4'),
          t('home.investCtaFeature5')
        ]}
        ctaText={t('home.investNow')}
        ctaHref="/invest"
        imageSrc="https://customer-assets.emergentagent.com/job_98176c5a-4141-45aa-866d-b1c1a8cb31e7/artifacts/v6t9afwt_7a40f4_b4ea21305c7748ec9f8397d8456570c4~mv2.avif"
        imageAlt="Accretion Axiom Real Estate"
      />

      {/* Why AAC - Formal Design - Todo centrado en móvil */}
      <section className="py-12 md:py-20 bg-gray-50" data-testid="why-invest-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-sm tracking-[0.2em] text-gray-500 mb-3">{t('home.whyFundLabel')}</p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 tracking-tight" data-testid="why-invest-title">
              {t('home.whyTitle')}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm md:text-base">
              {t('home.whySubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Feature 1 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-8 hover:shadow-lg hover:border-gray-300 transition-all duration-300 text-center md:text-left" data-testid="feature-accessible">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Coins className="text-white" size={20} />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg md:text-xl font-bold mb-1" data-testid="feature-accessible-title">{t('home.feature1Title')}</h3>
                  <p className="text-sm md:text-base font-semibold text-gray-900 mb-2" data-testid="feature-accessible-subtitle">{t('home.feature1Subtitle')}</p>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-testid="feature-accessible-description">
                    {t('home.feature1Desc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-8 hover:shadow-lg hover:border-gray-300 transition-all duration-300 text-center md:text-left" data-testid="feature-nowork">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-white" size={20} />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg md:text-xl font-bold mb-1" data-testid="feature-nowork-title">{t('home.feature2Title')}</h3>
                  <p className="text-sm md:text-base font-semibold text-gray-900 mb-2" data-testid="feature-nowork-subtitle">{t('home.feature2Subtitle')}</p>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-testid="feature-nowork-description">
                    {t('home.feature2Desc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-8 hover:shadow-lg hover:border-gray-300 transition-all duration-300 text-center md:text-left" data-testid="feature-returns">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="text-white" size={20} />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg md:text-xl font-bold mb-1" data-testid="feature-returns-title">{t('home.feature3Title')}</h3>
                  <p className="text-sm md:text-base font-semibold text-gray-900 mb-2">{t('home.feature3Subtitle')}</p>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-testid="feature-returns-description">
                    {t('home.feature3Desc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-8 hover:shadow-lg hover:border-gray-300 transition-all duration-300 text-center md:text-left" data-testid="feature-culture">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="text-white" size={20} />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg md:text-xl font-bold mb-1" data-testid="feature-culture-title">{t('home.feature4Title')}</h3>
                  <p className="text-sm md:text-base font-semibold text-gray-900 mb-2">{t('home.feature4Subtitle')}</p>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-testid="feature-culture-description">
                    <strong>{t('home.feature4Desc')}</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Section - New Premium Design */}
      <ReferralBanner />

      {/* Off-Market Opportunities - Fotos full width GRANDES */}
      <section className="py-0 bg-[#f5f5f0]" data-testid="offmarket-opportunities-section">
        <div className="relative">
          {/* Fotos full width - apiladas en móvil, lado a lado en desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <img 
              src="https://static.wixstatic.com/media/7a40f4_515be14bd65e49aaa771633e04a80c31~mv2.jpg"
              alt="Villa moderna con piscina"
              className="w-full h-[250px] md:h-[650px] object-cover"
            />
            <img 
              src="https://static.wixstatic.com/media/7a40f4_b0c9d423abf74b199e3fc5498a37a20d~mv2.jpg"
              alt="Villa de lujo nocturna"
              className="w-full h-[250px] md:h-[650px] object-cover"
            />
          </div>
          
          {/* Tarjeta blanca - centrada encima de las imágenes */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white py-6 md:py-10 px-6 md:px-12 shadow-2xl z-10 text-center mx-4 rounded-lg lg:rounded-none w-[90%] max-w-md lg:w-auto lg:max-w-none">
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3 leading-tight" data-testid="offmarket-title">
              {t('home.offMarketOpportunities').split(' ').slice(0, 2).join(' ')}<br />{t('home.offMarketOpportunities').split(' ').slice(2).join(' ')}
            </h2>
            <p className="text-[10px] md:text-sm text-gray-600 mb-2 md:mb-3 max-w-sm mx-auto" data-testid="offmarket-subtitle">
              {t('home.offMarketOpportunitiesDesc')}
            </p>
            <ul className="text-[10px] md:text-sm text-gray-700 space-y-0.5 md:space-y-1 mb-3 md:mb-5">
              <li data-testid="property-1">- {t('home.offMarketProperty1')}</li>
              <li data-testid="property-2">- {t('home.offMarketProperty2')}</li>
            </ul>
            <a 
              href="/off-market" 
              className="bg-black text-white px-4 md:px-6 py-2 md:py-3 text-[10px] md:text-sm font-semibold hover:bg-gray-800 transition-all inline-flex items-center justify-center"
              data-testid="offmarket-cta-btn"
            >
              {t('home.moreInfo')}
              <ArrowRight className="ml-1 md:ml-2" size={12} />
            </a>
          </div>
        </div>
      </section>

      {/* Track Record - Stats */}
      <section id="track-record" className="py-16 bg-gray-900 text-white" data-testid="track-record-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] text-gray-500 mb-3">{t('home.trackRecordLabel')}</p>
            <h2 className="text-3xl md:text-4xl font-bold" data-testid="track-record-title">{t('home.trackRecordTitle')}</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4" data-testid="stat-experience">
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white" data-testid="stat-experience-value">7</div>
              <div className="text-gray-400 text-xs" data-testid="stat-experience-label">{t('home.statExperience')}</div>
            </div>
            <div className="text-center p-4" data-testid="stat-profit">
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white" data-testid="stat-profit-value">+$70k</div>
              <div className="text-gray-400 text-xs" data-testid="stat-profit-label">{t('home.statProfit')}</div>
            </div>
            <div className="text-center p-4" data-testid="stat-deals">
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white" data-testid="stat-deals-value">+€10M</div>
              <div className="text-gray-400 text-xs" data-testid="stat-deals-label">{t('home.statDeals')}</div>
            </div>
            <div className="text-center p-4" data-testid="stat-markets">
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white" data-testid="stat-markets-value">+10</div>
              <div className="text-gray-400 text-xs" data-testid="stat-markets-label">{t('home.statMarkets')}</div>
            </div>
            <div className="text-center p-4" data-testid="stat-contacts">
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white" data-testid="stat-contacts-value">+100</div>
              <div className="text-gray-400 text-xs" data-testid="stat-contacts-label">{t('home.statContacts')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Investor Registration Form */}
      <section id="investor-form" className="py-12 bg-gray-100" data-testid="investor-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold mb-2" data-testid="investor-form-title">
              {t('home.investorFormTitle')}
            </h2>
            <p className="text-base text-gray-600" data-testid="investor-form-subtitle">
              {t('home.investorFormSubtitle')}
            </p>
          </div>

          <form onSubmit={handleInvestorSubmit} className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-testid="investor-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor="full_name">{t('home.investorFormName')} *</label>
                <input
                  type="text"
                  id="full_name"
                  required
                  value={investorForm.full_name}
                  onChange={(e) => setInvestorForm({ ...investorForm, full_name: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black text-sm"
                  placeholder={t('home.investorFormPlaceholderName')}
                  data-testid="investor-form-name-input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor="email">{t('home.investorFormEmail')} *</label>
                <input
                  type="email"
                  id="email"
                  required
                  value={investorForm.email}
                  onChange={(e) => setInvestorForm({ ...investorForm, email: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black text-sm"
                  placeholder={t('home.investorFormPlaceholderEmail')}
                  data-testid="investor-form-email-input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor="phone">{t('home.investorFormPhone')}</label>
                <input
                  type="tel"
                  id="phone"
                  value={investorForm.phone}
                  onChange={(e) => setInvestorForm({ ...investorForm, phone: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black text-sm"
                  placeholder="+34 XXX XXX XXX"
                  data-testid="investor-form-phone-input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor="investment_amount">{t('home.investorFormAmount')}</label>
                <select
                  id="investment_amount"
                  value={investorForm.investment_amount}
                  onChange={(e) => setInvestorForm({ ...investorForm, investment_amount: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black text-sm"
                  data-testid="investor-form-amount-input"
                >
                  <option value="">{t('home.investorFormSelectRange')}</option>
                  <option value="€10,000 - €25,000">€10,000 - €25,000</option>
                  <option value="€25,000 - €50,000">€25,000 - €50,000</option>
                  <option value="€50,000 - €100,000">€50,000 - €100,000</option>
                  <option value="€100,000+">€100,000+</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor="message">{t('home.investorFormMessage')}</label>
              <textarea
                id="message"
                value={investorForm.message}
                onChange={(e) => setInvestorForm({ ...investorForm, message: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black text-sm"
                placeholder={t('home.investorFormPlaceholderMessage')}
                data-testid="investor-form-message-input"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-black text-white px-6 py-3 rounded text-sm font-semibold hover:bg-gray-800 transition-all duration-200"
              data-testid="investor-form-submit-btn"
            >
              {t('home.investorFormSubmit')}
            </button>

            {investorStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded mt-3 text-sm" data-testid="investor-form-success-message">
                ✓ {t('home.investorFormSuccess')}
              </div>
            )}

            {investorStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded mt-3 text-sm" data-testid="investor-form-error-message">
                ✗ {t('home.investorFormError')}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Newsletter Section - Con foto del yate y filtro menos oscuro */}
      <section id="contact" className="py-16 bg-gray-800 text-white relative overflow-hidden" data-testid="newsletter-section">
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_web-mejora-1/artifacts/tckpj879_11062b_3e27d48e57b146719ab4fbbbcc67986b~mv2.avif)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="newsletter-title">
            {t('home.newsletterTitle')}
          </h2>
          <p className="text-gray-200 mb-8" data-testid="newsletter-subtitle">
            {t('home.newsletterSubtitle')}
          </p>

          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder={t('home.newsletterPlaceholder')}
              className="flex-1 px-6 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              className="bg-white text-black px-8 py-3 font-semibold hover:bg-gray-100 transition-all duration-200"
              data-testid="newsletter-submit-btn"
            >
              {t('home.newsletterButton')}
            </button>
          </form>

          {newsletterStatus === 'success' && (
            <p className="mt-4 text-green-400" data-testid="newsletter-success-message">✓ {t('home.newsletterSuccess')}</p>
          )}

          {newsletterStatus === 'error' && (
            <p className="mt-4 text-red-400" data-testid="newsletter-error-message">✗ {t('home.newsletterError')}</p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;